<div class="answer-form-card" [class.disabled]="hasCustomerAnswer">
  <!-- Card Header -->
  <div class="card-header">
    <span class="answer-number">Answer {{ index + 1 }}</span>
    <div class="card-actions">
      <div class="comment-options">
        <!-- Comment checkboxes: disabled on edit -->
        <span class="checkbox-group">
          <p-checkbox
            [formControl]="formGroup.controls.enableComment"
            [binary]="true"
            inputId="enableComment-{{ index }}"
            (onChange)="onEnableCommentChange($event.checked)"
            [disabled]="isEditMode"
          />
          <label
            [for]="'enableComment-' + index"
            class="checkbox-label"
            [class.disabled]="isEditMode"
            >Enable comment</label
          >
        </span>

        <span class="checkbox-group">
          <p-checkbox
            [formControl]="formGroup.controls.requireComment"
            [binary]="true"
            inputId="requireComment-{{ index }}"
            (onChange)="onRequireCommentChange($event.checked)"
            [disabled]="isEditMode || !formGroup.controls.enableComment.value"
          />
          <label
            [for]="'requireComment-' + index"
            class="checkbox-label"
            [class.disabled]="
              isEditMode || !formGroup.controls.enableComment.value
            "
          >
            Require comment
          </label>
        </span>
      </div>

      <!-- Delete button: hidden on edit -->
      @if (canDelete) {
        <p-button
          icon="pi pi-trash"
          severity="danger"
          [text]="true"
          [rounded]="true"
          size="small"
          pTooltip="Remove answer"
          tooltipPosition="top"
          (onClick)="onRemove()"
        />
      }
    </div>
  </div>

  <!-- Card Body -->
  <div class="card-body" [formGroup]="formGroup">
    <!-- Answer Text + Offer Type - INLINE ROW -->
    <div class="form-row">
      <!-- Answer Text: only disabled if hasCustomerAnswer -->
      <div class="form-field half">
        <label [for]="'answer-' + index" class="field-label">
          Answer Text <span class="required">*</span>
        </label>
        <p-select
          [inputId]="'answer-' + index"
          [formControl]="formGroup.controls.answer"
          [options]="predefinedAnswers"
          optionLabel="answer"
          optionValue="answer"
          [editable]="true"
          [filter]="true"
          filterBy="answer"
          placeholder="Select or type..."
          [disabled]="hasCustomerAnswer"
          styleClass="answer-select"
          appendTo="body"
        />
        @if (
          formGroup.controls.answer.invalid && formGroup.controls.answer.touched
        ) {
          <small class="field-error">
            @if (formGroup.controls.answer.errors?.["required"]) {
              Required
            } @else if (formGroup.controls.answer.errors?.["minlength"]) {
              Min {{ validation.answer.minLength }} chars
            } @else if (formGroup.controls.answer.errors?.["maxlength"]) {
              Max {{ validation.answer.maxLength }} chars
            }
          </small>
        }
      </div>

      <!-- Offer Type: show text on edit, dropdown on create -->
      <div
        class="form-field half"
        [formGroup]="formGroup.controls.offerApplicableItem"
      >
        <label [for]="'offerType-' + index" class="field-label"
          >Offer Type</label
        >
        @if (isEditMode) {
          <input
            pInputText
            [id]="'offerType-' + index"
            [value]="offerTypeDisplayName"
            [disabled]="true"
            class="readonly-input"
          />
        } @else {
          <p-select
            [inputId]="'offerType-' + index"
            [formControl]="
              formGroup.controls.offerApplicableItem.controls.itemType
            "
            [options]="offerTypeOptions"
            optionLabel="displayName"
            optionValue="value"
            placeholder="Select offer..."
            styleClass="offer-type-select"
            appendTo="body"
          />
        }
      </div>
    </div>

    <!-- Free Product Fields - compact layout -->
    @if (showProductFields) {
      <div
        class="offer-fields"
        [formGroup]="formGroup.controls.offerApplicableItem"
      >
        <!-- Product + Max Cycles + Quantity - all in one row on desktop -->
        <div class="form-row form-row-3col">
          <!-- Product: show text on edit -->
          <div class="form-field flex-2">
            <label [for]="'product-' + index" class="field-label">
              Product <span class="required">*</span>
            </label>
            @if (isEditMode) {
              <input
                pInputText
                [id]="'product-' + index"
                [value]="productDisplayName"
                [disabled]="true"
                class="readonly-input"
              />
            } @else {
              <p-autoComplete
                [inputId]="'product-' + index"
                [(ngModel)]="selectedProduct"
                [ngModelOptions]="{ standalone: true }"
                [suggestions]="productSuggestions"
                (completeMethod)="searchProducts($event)"
                (onSelect)="onProductSelect($event.value)"
                field="Title"
                [dropdown]="true"
                [minLength]="0"
                placeholder="Search product..."
                styleClass="product-autocomplete"
                appendTo="body"
              >
                <ng-template let-product pTemplate="item">
                  <div class="product-suggestion">
                    <span class="product-name">{{ product.Title }}</span>
                    @if (product.Sku) {
                      <span class="product-sku">{{ product.Sku }}</span>
                    }
                  </div>
                </ng-template>
              </p-autoComplete>
            }
          </div>

          <!-- Max Cycles: show text on edit -->
          <div class="form-field flex-1">
            <label [for]="'maxCycles-' + index" class="field-label"
              >Max Cycles</label
            >
            @if (isEditMode) {
              <input
                pInputText
                [id]="'maxCycles-' + index"
                [value]="maxCyclesDisplayName"
                [disabled]="true"
                class="readonly-input"
              />
            } @else {
              <p-select
                [inputId]="'maxCycles-' + index"
                [formControl]="
                  formGroup.controls.offerApplicableItem.controls.maxCycles
                "
                [options]="maxCyclesOptions"
                optionLabel="displayName"
                optionValue="value"
                placeholder="Cycles"
                appendTo="body"
              />
            }
          </div>

          <!-- Quantity: show text on edit -->
          <div class="form-field flex-1">
            <label [for]="'quantity-' + index" class="field-label"
              >Quantity</label
            >
            @if (isEditMode) {
              <input
                pInputText
                [id]="'quantity-' + index"
                [value]="quantityDisplayName"
                [disabled]="true"
                class="readonly-input"
              />
            } @else {
              <p-select
                [inputId]="'quantity-' + index"
                [formControl]="
                  formGroup.controls.offerApplicableItem.controls.quantity
                "
                [options]="quantityOptions"
                optionLabel="displayName"
                optionValue="value"
                placeholder="Quantity"
                appendTo="body"
              />
            }
          </div>
        </div>
      </div>
    }

    <!-- Discount/Coupon Fields - compact -->
    @if (showCouponFields) {
      <div
        class="offer-fields"
        [formGroup]="formGroup.controls.offerApplicableItem"
      >
        <!-- Coupon: show text on edit -->
        <div class="form-field">
          <label [for]="'coupon-' + index" class="field-label">
            Coupon <span class="required">*</span>
          </label>
          @if (isEditMode) {
            <input
              pInputText
              [id]="'coupon-' + index"
              [value]="couponDisplayName"
              [disabled]="true"
              class="readonly-input"
            />
          } @else {
            <p-autoComplete
              [inputId]="'coupon-' + index"
              [(ngModel)]="selectedCoupon"
              [ngModelOptions]="{ standalone: true }"
              [suggestions]="couponSuggestions"
              (completeMethod)="searchCoupons($event)"
              (onSelect)="onCouponSelect($event.value)"
              field="Code"
              [dropdown]="true"
              [minLength]="0"
              placeholder="Search coupon..."
              styleClass="coupon-autocomplete"
              appendTo="body"
            >
              <ng-template let-coupon pTemplate="item">
                <div class="coupon-suggestion">
                  <span class="coupon-code">{{ coupon.Code }}</span>
                  @if (coupon.Amount) {
                    <span class="coupon-amount">{{
                      coupon.Amount | currency
                    }}</span>
                  }
                </div>
              </ng-template>
            </p-autoComplete>
          }
        </div>
      </div>
    }

    <!-- Custom Offer Text (Phrase): only disabled if hasCustomerAnswer -->
    @if (showPhraseField) {
      <div
        class="offer-fields"
        [formGroup]="formGroup.controls.offerApplicableItem"
      >
        <div class="form-field">
          <div class="field-label-row">
            <label [for]="'phrase-' + index" class="field-label"
              >Custom Offer Text</label
            >
            <app-dynamic-fields-popover />
          </div>
          <input
            pInputText
            [id]="'phrase-' + index"
            [formControl]="
              formGroup.controls.offerApplicableItem.controls.phrase
            "
            placeholder="Enter custom offer text with dynamic fields..."
            [disabled]="hasCustomerAnswer"
            class="phrase-input"
          />
          <small class="field-hint">
            Use {{ '{{ProductName}}' }} or {{ '{{CouponAmount}}' }} for dynamic
            values
          </small>
        </div>
      </div>
    }
  </div>
</div>
